import { useEffect, useMemo, useState } from "react";

import {
  User,
  UserOrg,
  UserOrgForm,
  userMutationFunctions,
} from "@/models/user";
import { CoreDataModel, SecurityRole } from "@/models";

export const RECORD_TYPE_LABELS: Record<string, string> = {
  CHLD: "Child",
  FAMI: "Family",
  LANG: "Language",
  SLNG: "Supplemental DLL",
  ENRL: "Enrollment",
  CLEN: "Classroom Enrollment",
  STFF: "Staff",
  SASS: "Staff Assignment",
  SCLR: "Staff Classroom",
  CLRM: "Classroom",
  ATTN: "Attendance",
  PRGM: "Education Programs",
};

export const ADDITIONAL_PERMISSION_LABELS: Record<string, string> = {
  Extracts: "Extracts",
  Certifier: "Certifier",
  ReportsEOY: "Reports (EOY)",
  ReportsFall: "Reports (Fall)",
  ReportsMonthly: "Reports (Monthly)",
  CALPADSSync: "CALPADS Sync",
};

// Module-level constant to avoid creating new array reference on every render
const EMPTY_ROLES: SecurityRole[] = [];

export function useOrgPermissions(
  org: UserOrg,
  coreData: CoreDataModel,
  leaid: string,
  user: User<UserOrg>,
  onSuccess?: () => void
) {
  const securityRoles = coreData?.securityRoles ?? EMPTY_ROLES;

  const idToCode = useMemo<Record<number, string>>(
    () =>
      Object.fromEntries(
        securityRoles.map((r) => [r.CAPSDACRoleId, r.CAPSDACRoleCode])
      ),
    [securityRoles]
  );
  const codeToId = useMemo<Record<string, number>>(
    () =>
      Object.fromEntries(
        securityRoles.map((r) => [r.CAPSDACRoleCode, r.CAPSDACRoleId])
      ),
    [securityRoles]
  );

  const orgRoles = useMemo(
    () => securityRoles.filter((r) => r.ScrtyGrpKey === org.orgType),
    [securityRoles, org.orgType]
  );

  const recordTypes = useMemo(() => {
    const order = Object.keys(RECORD_TYPE_LABELS);
    const derived = [
      ...new Set(
        orgRoles
          .filter(
            (r) =>
              r.CAPSDACRoleCode.endsWith("View") ||
              r.CAPSDACRoleCode.endsWith("Edit")
          )
          .map((r) => r.CAPSDACRoleCode.replace(/View$|Edit$/, ""))
      ),
    ];
    return derived.sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [orgRoles]);

  const additionalPermissions = useMemo(
    () =>
      orgRoles
        .filter(
          (r) =>
            !r.CAPSDACRoleCode.endsWith("View") &&
            !r.CAPSDACRoleCode.endsWith("Edit") &&
            r.CAPSDACRoleCode !== "ADMIN"
        )
        .map((r) => r.CAPSDACRoleCode),
    [orgRoles]
  );

  const numericRoleIds: number[] = org.roleIds ?? [];
  const currentRoles = numericRoleIds.map((id) => idToCode[id]).filter(Boolean);

  const adminRole = useMemo(
    () =>
      securityRoles.find(
        (r: SecurityRole) =>
          r.CAPSDACRoleCode === "ADMIN" && r.ScrtyGrpKey === org.orgType
      ),
    [securityRoles, org.orgType]
  );

  const getInitialState = (roleIds: number[], roles: string[]) => ({
    isOrgAdmin: adminRole ? roleIds.includes(adminRole.CAPSDACRoleId) : false,
    viewPerms: Object.fromEntries(
      recordTypes.map((rt) => [rt, roles.includes(`${rt}View`)])
    ),
    editPerms: Object.fromEntries(
      recordTypes.map((rt) => [rt, roles.includes(`${rt}Edit`)])
    ),
    additionalPerms: Object.fromEntries(
      additionalPermissions.map((p) => [p, roles.includes(p)])
    ),
  });

  const [isOrgAdmin, setIsOrgAdmin] = useState<boolean>(
    () => getInitialState(numericRoleIds, currentRoles).isOrgAdmin
  );
  const [viewPerms, setViewPerms] = useState<Record<string, boolean>>(
    () => getInitialState(numericRoleIds, currentRoles).viewPerms
  );
  const [editPerms, setEditPerms] = useState<Record<string, boolean>>(
    () => getInitialState(numericRoleIds, currentRoles).editPerms
  );
  const [additionalPerms, setAdditionalPerms] = useState<
    Record<string, boolean>
  >(() => getInitialState(numericRoleIds, currentRoles).additionalPerms);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const updatedRoleIds = org.roleIds ?? [];
    const updatedRoles = updatedRoleIds
      .map((id) => idToCode[id])
      .filter(Boolean);
    const state = getInitialState(updatedRoleIds, updatedRoles);
    setIsOrgAdmin(state.isOrgAdmin);
    setViewPerms(state.viewPerms);
    setEditPerms(state.editPerms);
    setAdditionalPerms(state.additionalPerms);
    setIsDirty(false);
  }, [org.roleIds, idToCode, recordTypes, additionalPermissions, adminRole]);

  const dirty = () => setIsDirty(true);

  const handleReset = () => {
    const roleIds = org.roleIds ?? [];
    const roles = roleIds.map((id) => idToCode[id]).filter(Boolean);
    const state = getInitialState(roleIds, roles);
    setIsOrgAdmin(state.isOrgAdmin);
    setViewPerms(state.viewPerms);
    setEditPerms(state.editPerms);
    setAdditionalPerms(state.additionalPerms);
    setIsDirty(false);
  };

  const toggleOrgAdmin = (value: boolean | ((prev: boolean) => boolean)) => {
    setIsOrgAdmin(value);
    dirty();
  };

  const toggle = (type: "view" | "edit", rt: string) => {
    if (type === "edit") {
      setEditPerms((prev) => {
        const next = !prev[rt];
        if (next) setViewPerms((vp) => ({ ...vp, [rt]: true }));
        return { ...prev, [rt]: next };
      });
    } else {
      setViewPerms((prev) => {
        const next = !prev[rt];
        if (!next) setEditPerms((ep) => ({ ...ep, [rt]: false }));
        return { ...prev, [rt]: next };
      });
    }
    dirty();
  };

  const toggleAdditionalPerm = (p: string) => {
    setAdditionalPerms((prev) => ({ ...prev, [p]: !prev[p] }));
    dirty();
  };

  const setAllView = () => {
    const allOn = recordTypes.every((rt) => viewPerms[rt]);
    setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allOn])));
    if (allOn)
      setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
    dirty();
  };

  const setAllEdit = () => {
    const allOn = recordTypes.every((rt) => editPerms[rt]);
    setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allOn])));
    if (!allOn)
      setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, true])));
    dirty();
  };

  const setFullAccess = () => {
    const allOn =
      recordTypes.every((rt) => viewPerms[rt] && editPerms[rt]) &&
      additionalPermissions.every((p) => additionalPerms[p]);
    setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allOn])));
    setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allOn])));
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, !allOn]))
    );
    dirty();
  };

  const setAllAdditional = () => {
    const allOn = additionalPermissions.every((p) => additionalPerms[p]);
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, !allOn]))
    );
    dirty();
  };

  const userMutateFns = userMutationFunctions(leaid, () => {
    setIsDirty(false);
    onSuccess?.();
  });

  const handleSave = () => {
    const managedCodes = new Set([
      ...recordTypes.map((rt) => `${rt}View`),
      ...recordTypes.map((rt) => `${rt}Edit`),
      ...additionalPermissions,
    ]);
    const unmanagedRoleIds = numericRoleIds.filter((id) => {
      const code = idToCode[id];
      return !managedCodes.has(code) && code !== "ADMIN";
    });
    const managedRoleIds = [
      ...recordTypes
        .filter((rt) => viewPerms[rt])
        .map((rt) => codeToId[`${rt}View`]),
      ...recordTypes
        .filter((rt) => editPerms[rt])
        .map((rt) => codeToId[`${rt}Edit`]),
      ...additionalPermissions
        .filter((p) => additionalPerms[p])
        .map((p) => codeToId[p]),
    ].filter(Boolean);

    const adminIds = isOrgAdmin && adminRole ? [adminRole.CAPSDACRoleId] : [];
    const roleIds = [...unmanagedRoleIds, ...adminIds, ...managedRoleIds];

    userMutateFns.handleUserOrgSave({
      ...user,
      userOrgs: [new UserOrgForm({ ...org, roleIds })],
    });
  };

  const handleDelete = () => {
    userMutateFns.handleUserOrgDelete({
      ...user,
      userOrgs: [{ esiIdPath: org.esiIdPath, esiId: org.esiId }] as any,
    });
  };

  return {
    isOrgAdmin,
    setIsOrgAdmin: toggleOrgAdmin,
    viewPerms,
    editPerms,
    additionalPerms,
    isDirty,
    adminRole,
    mutationState: userMutateFns.mutationState,
    toggle,
    toggleAdditionalPerm,
    setAllView,
    setAllEdit,
    setFullAccess,
    setAllAdditional,
    handleSave,
    handleDelete,
    handleReset,
    recordTypes,
    additionalPermissions,
  };
}
